'use client';

import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    LVForms?: { render: (id: number, containerId: string) => void };
  }
}

const EMBED_SRC = 'https://crmapi.awarious.com/forms/embed.js';

/**
 * Renders a Lead Visibility / CRM form by its numeric id (e.g. 9 = Contact Us,
 * 8 = Book a Demo). The embed relies on a third-party <script>, which CANNOT be
 * loaded by pasting it into a CMS rich-text / HTML field — browsers never run
 * scripts injected via innerHTML/dangerouslySetInnerHTML. So we inject it
 * programmatically here (createElement + appendChild, which DOES execute) and
 * call LVForms.render once it's ready (same technique as LeadFormHero).
 */
export function CrmFormBlockRenderer({ block }: { block: any }) {
  const formId = Number(block?.formId);
  const containerId = `lv-form-${formId}`;
  const done = useRef(false);

  useEffect(() => {
    if (!formId) return;
    if (!done.current) {
      done.current = true;
      const render = () => window.LVForms?.render(formId, containerId);
      if (window.LVForms) {
        render();
      } else {
        // Reuse a single embed.js across all CRM form blocks on the page.
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${EMBED_SRC}"]`);
        if (existing) {
          existing.addEventListener('load', render);
        } else {
          const script = document.createElement('script');
          script.src = EMBED_SRC;
          script.async = true;
          script.addEventListener('load', render);
          document.head.appendChild(script);
        }
      }
    }
  }, [formId, containerId]);

  if (!formId) return null;

  const isAlt = ((block._sectionIndex ?? 0) % 2) === 1;

  return (
    <section className="py-16 lg:py-24" style={{ backgroundColor: isAlt ? 'var(--color-t-section-alt)' : 'var(--color-t-section-light)' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {block.heading && (
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-center mb-10" style={{ color: 'var(--color-t-heading)' }}>
            {block.heading}
          </h2>
        )}
        <div id={containerId} className="min-h-[200px]">
          {/* Loading skeleton while the CRM form loads */}
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-gray-100 rounded-lg" />
            <div className="h-10 bg-gray-100 rounded-lg" />
            <div className="h-10 bg-gray-100 rounded-lg" />
            <div className="h-12 rounded-lg" style={{ backgroundColor: 'color-mix(in srgb, var(--color-t-cta) 30%, transparent)' }} />
          </div>
        </div>
      </div>
    </section>
  );
}
